// 🔧 reset.js
// ✅ Purpose: Handles both reset email requests and password resets from oobCode
// ✅ Supports: Direct email input + Firebase resetPassword link via email_action.js

import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useLocation,
  useSearchParams,
  Link,
} from "react-router-dom";
import {
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, sendPasswordReset } from "../../firebase";
import { logger } from "../../utils/logger";
import { showStatus } from "../../utils/status_helpers";
import SeoHead from "../../components/seo_head";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

function Reset() {

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [status, setStatus] = useState(null);
  const [verified, setVerified] = useState(false);
  const [oobCode, setOobCode] = useState(null);
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = location.state?.from?.pathname || "/dashboard?reset";

  useEffect(() => {
    if (loading) return;
    if (user) navigate(from, { replace: true });
  }, [user, loading, navigate, from]);

  useEffect(() => {
    const code = searchParams.get("oobCode");

    if (!code) return;

    verifyPasswordResetCode(auth, code)
      .then(() => {
        setVerified(true);
        setOobCode(code);
      })
      .catch((err) => {
        logger.error(
          "❌ Invalid or expired reset link:",
          { value: err }
        );

        showStatus(setStatus, {
          type: "error",
          message:
            "This password reset link is invalid or has expired.",
        });
      });
  }, [searchParams]);

  // ✅ Request reset email
  const handleResetRequest = async () => {
    if (!email) {

      showStatus(setStatus, {
        type: "error",
        message: "Please enter your email.",
      });

      return;
    }

    try {
      await sendPasswordReset(email);

      trackEvent({ action: GA_EVENTS.PASSWORD_RESET_REQUESTED, parameters: { source_page: window.location.pathname } });

      showStatus(setStatus, {
        type: "success",
        message: "Reset link sent. Check your inbox.",
      });

    } catch (err) {
      logger.error("❌ Reset email error:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message:
          "Unable to send password reset email. Please try again.",
      });
    }
  };

  // ✅ Submit new password with oobCode
  const handlePasswordUpdate = async () => {
    if (!newPassword || newPassword.length < 6) {

      showStatus(setStatus, {
        type: "error",
        message: "Password must be at least 6 characters.",
      });

      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);

      trackEvent({ action: GA_EVENTS.PASSWORD_RESET_COMPLETED, parameters: { source_page: window.location.pathname } });

      showStatus(setStatus, {
        type: "success",
        message: "Your password has been updated! Redirecting...",
      });

      setTimeout(() => {
        navigate("/login?reset=success", {
          replace: true,
        });
      }, 4000);

    } catch (err) {
      logger.error("❌ Failed to update password:", { value: err });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to reset password. Please try again.",
      });

    }
  };

  return (
  <>
    <SeoHead
      title="Reset Password | Let's Go Vote"
      description="Reset your Let's Go Vote account password."
      path="/reset"
      noindex={true}
    />
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🔐 Reset Password</h1>
          <p className="lead text-muted">
            {verified
              ? "Set a new password below"
              : "Enter your email to request a password reset link"}
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">

        {status?.message && (
          <div
           role="alert"
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-xs-12 col-md-6">
              <div className="bg-white p-4 rounded shadow text-start">

                {verified && (
                  <div className="alert alert-warning">
                    🔐 Reset your password below
                  </div>
                )} 

                {!verified ? (
                  <>
                    <div className="mb-3">
                      <label>Email Address</label>
                      <input
                        type="email"
                        className="form-control"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                      />
                    </div>

                    <div className="d-grid mb-3">

                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleResetRequest}
                        >

                        Send password reset email
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3">
                      <label>New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter a new password"
                        required
                      />
                    </div>

                    <div className="d-grid mb-3">

                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handlePasswordUpdate}
                        >

                        Update Password
                      </button>
                    </div>
                  </>
                )}

                <div className="mt-4 text-center text-muted">
                  Don’t have an account? <Link to="/register">Register</Link><br />
                  Already have one? <Link to="/login?reset">Login</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
  );
}

export default Reset;
